import React, { useState, useEffect } from "react";
import { FiFolder, FiCalendar, FiExternalLink } from "react-icons/fi";
import DocumentTitle from "../../components/ui/DocumentTitle";
import { projectsService } from "../../services/projectsService";
import "./Projects.css";

function Projects() {
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);

  const getDefaultImage = (type) => {
    const images = {
      'Web App': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      'Backend': 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
      'Mobile App': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80',
      'ML/AI': 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&q=80',
    };
    return images[type] || images['Web App'];
  };

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setIsLoading(true);
        const projectsData = await projectsService.getAll();
        const transformed = projectsData.map(item => ({
          id: item.id,
          title: item.project.title,
          type: item.project.project_type || 'Projet',
          description: item.project.description || 'Aucune description fournie.',
          technologies: item.project.technologies || [],
          status: item.project.status || 'planned',
          date: item.project.start_date || '2024',
          image: item.project.image_url || getDefaultImage(item.project.project_type),
          url: item.project.url
        }));
        // Tri par date décroissante : le plus récent en premier
        transformed.sort((a, b) => b.date.localeCompare(a.date));
        setProjects(transformed);
      } catch (e) {
        setError('Échec de synchronisation avec les archives de projets');
      } finally {
        setIsLoading(false);
      }
    };
    loadProjects();
  }, []);

  const projectTypes = ['all', ...new Set(projects.map(p => p.type))];
  const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.type === filter);

  const getStatusText = (s) => {
    const map = { 'completed': 'Terminé', 'in-progress': 'En cours', 'planned': 'Planifié' };
    return map[s] || s;
  };

  return (
    <div className="page projects-page">
      <DocumentTitle title="Projets" />

      <header className="page-header">
        <div className="container">
          <h1 className="page-title"><FiFolder /> Mes Projets</h1>
        </div>
      </header>

      <main className="projects-main">
        <div className="container">
          <div className="projects-filters">
            {projectTypes.map(type => (
              <button 
                key={type} 
                className={`filter-btn ${filter === type ? 'active' : ''}`} 
                onClick={() => setFilter(type)}
              >
                {type === 'all' ? 'Tous' : `${type}`}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="projects-grid">
               {[1, 2, 3].map(i => (
                 <div key={i} className="project-card-skeleton skeleton"></div>
               ))}
            </div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : (
            <div className="projects-grid">
              {filteredProjects.map((project) => (
                <article key={project.id} className="project-card">
                  <div className="project-image" style={{ backgroundImage: `url(${project.image})` }}>
                    <span className="project-type-badge">{project.type}</span>
                  </div>
                  
                  <div className="project-info">
                    <h3>
                      {project.title}
                      {project.url && <FiExternalLink className="project-link-icon" />}
                    </h3>
                    <p>{project.description}</p>
                    
                    <div className="project-technologies">
                      {project.technologies?.map(tech => (
                        <span key={tech} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>

                  <div className="project-meta">
                    <span className="project-date"><FiCalendar /> {project.date}</span>
                    <div className="project-status">
                      <span className="status-dot" style={{backgroundColor: project.status === 'completed' ? 'var(--primary-color)' : 'var(--warning-color)'}}></span>
                      {getStatusText(project.status)}
                    </div>
                  </div>
                  {project.url && (
                    <a href={project.url} target="_blank" rel="noopener noreferrer" className="main-card-link" aria-label={project.title} />
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Projects;